import React, { useState } from 'react';
import { MdClose, MdEmail, MdLocalHospital, MdLocationOn, MdPerson, MdPhone, MdSend } from 'react-icons/md';
import { hospitalAPI } from '../services/api';

const EMPTY = {
  contactName: '',
  hospitalName: '',
  address: '',
  mobile: '',
  email: '',
  description: '',
};

export default function ContactUsModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!open) return null;

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const contactName = form.contactName.trim();
    const hospitalName = form.hospitalName.trim();
    const address = form.address.trim();
    const mobile = form.mobile.replace(/\D/g, '');
    const email = form.email.trim().toLowerCase();
    const description = form.description.trim();

    if (contactName.length < 2) return setError('Please enter your name.');
    if (hospitalName.length < 2) return setError('Please enter the hospital name.');
    if (address.length < 5) return setError('Please enter the hospital address.');
    if (!/^[6-9]\d{9}$/.test(mobile)) return setError('Enter a valid 10-digit Indian mobile number.');
    if (!email.includes('@')) return setError('Please enter a valid email address.');

    setLoading(true);
    try {
      const r = await hospitalAPI.submitContactInquiry({
        contactName,
        hospitalName,
        address,
        mobile,
        email,
        description,
      });
      if (r.error) {
        setError(r.error);
        return;
      }
      setSuccess(r.data?.message || 'Thank you! Our team will contact you shortly.');
      setForm(EMPTY);
    } catch (_) {
      setError('Could not send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError('');
    setSuccess('');
    onClose();
  };

  return (
    <div className="contact-modal-backdrop" onClick={handleClose} role="presentation">
      <div
        className="contact-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
      >
        <button type="button" className="contact-modal__close" onClick={handleClose} aria-label="Close">
          <MdClose />
        </button>

        <h2 id="contact-modal-title" className="contact-modal__title">Let&apos;s get you to the right place</h2>
        <p className="contact-modal__subtitle">Share a few details — our HCEN team will reach out to help with onboarding.</p>

        {success ? (
          <div className="contact-modal__success">
            <div className="contact-modal__success-icon">✓</div>
            <p>{success}</p>
            <button type="button" className="btn btn-primary" onClick={handleClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="contact-modal__form">
            <div className="form-group">
              <label className="form-label" htmlFor="contact-name">Your name</label>
              <div className="auth-input-wrap">
                <MdPerson className="auth-input-icon" aria-hidden />
                <input id="contact-name" className="form-input" placeholder="Full name" value={form.contactName}
                  onChange={e => update('contactName', e.target.value)} required />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="contact-hospital">Hospital name</label>
              <div className="auth-input-wrap">
                <MdLocalHospital className="auth-input-icon" aria-hidden />
                <input id="contact-hospital" className="form-input" placeholder="Hospital / clinic name" value={form.hospitalName}
                  onChange={e => update('hospitalName', e.target.value)} required />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="contact-address">Address</label>
              <div className="auth-input-wrap">
                <MdLocationOn className="auth-input-icon" aria-hidden />
                <input id="contact-address" className="form-input" placeholder="City, district, state" value={form.address}
                  onChange={e => update('address', e.target.value)} required />
              </div>
            </div>

            <div className="contact-modal__row">
              <div className="form-group">
                <label className="form-label" htmlFor="contact-mobile">Mobile</label>
                <div className="auth-input-wrap">
                  <MdPhone className="auth-input-icon" aria-hidden />
                  <input id="contact-mobile" className="form-input" placeholder="10-digit mobile" inputMode="numeric"
                    value={form.mobile} onChange={e => update('mobile', e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="contact-email">Email</label>
                <div className="auth-input-wrap">
                  <MdEmail className="auth-input-icon" aria-hidden />
                  <input id="contact-email" type="email" className="form-input" placeholder="you@hospital.com" value={form.email}
                    onChange={e => update('email', e.target.value)} required />
                </div>
              </div>
            </div>
            <p className="contact-modal__hint">Please use your official hospital email if available.</p>

            <div className="form-group">
              <label className="form-label" htmlFor="contact-desc">Message / description</label>
              <textarea id="contact-desc" className="form-input contact-modal__textarea" rows={4}
                placeholder="Tell us about your hospital, bed capacity, or questions about joining HCEN…"
                value={form.description} onChange={e => update('description', e.target.value)} />
            </div>

            {error && (
              <div className="contact-modal__error">{error}</div>
            )}

            <button type="submit" className="btn btn-primary btn-lg contact-modal__submit" disabled={loading}>
              {loading ? 'Sending…' : <><MdSend /> Send</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
